import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { FaSync } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import config from '../config';

const server_URL = config.SERVER_URL;

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [isSubuser, setIsSubuser] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    setIsSubuser(userData.is_subuser || false);

    if (userData.role_id === 1) {
      fetchCustomers();
    }

    fetchLogs(userData.role_id, userData.id, customerFilter, moduleFilter);
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    fetchLogs(userData.role_id, userData.id, customerFilter, moduleFilter);
  }, [customerFilter, moduleFilter]);

  const fetchCustomers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_id: userData.id,
          role_id: userData.role_id,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchLogs = async (role = userRole, userId = loggedUserId, custFilter = customerFilter, modFilter = moduleFilter) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/activity_logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_role: role,
          user_id: userId,
          is_subuser: userData.is_subuser || false,
          customer_id: role === 1 ? custFilter : undefined,
          module: modFilter || undefined,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setLogs(data.data);
      } else {
        showToast(data.error || 'Failed to fetch activity logs', 'error');
        setLogs([]);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      showToast('Error fetching activity logs', 'error');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setCustomerFilter('');
    setModuleFilter('');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    fetchLogs(userData.role_id, userData.id, '', '');
  };

  // Get unique modules from logs for filter
  const uniqueModules = [...new Set(logs.map(log => log.module))].sort();

  return (
    <div style={{ padding: '20px' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Activity Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View latest 500 user activity logs.
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {userRole === 1 && !isSubuser && (
            <FormControl sx={{ minWidth: 240 }}>
              <InputLabel>Filter by Customer</InputLabel>
              <Select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                label="Filter by Customer"
                size="small"
              >
                <MenuItem value="">All Customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.username} ({customer.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Module</InputLabel>
            <Select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              label="Filter by Module"
              size="small"
            >
              <MenuItem value="">All Modules</MenuItem>
              {uniqueModules.map((module) => (
                <MenuItem key={module} value={module}>
                  {module}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" color="warning" onClick={handleClear}>
              Clear
            </Button>
            <Button variant="outlined" onClick={() => fetchLogs()}>
              <FaSync className="me-2" />
              Refresh
            </Button>
          </Stack>
        </Paper>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {userRole === 1 && <TableCell><strong>Customer</strong></TableCell>}
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Module</strong></TableCell>
                  <TableCell><strong>Message</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={userRole === 1 ? 5 : 4} align="center" sx={{ py: 5 }}>
                      <Typography variant="h6" color="text.secondary">
                        No activity logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((row) => (
                    <TableRow key={row.id} hover>
                      {userRole === 1 && (
                        <TableCell>
                          <Typography variant="body2">{row.customer_name || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.customer_email || 'N/A'}</Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        {row.sub_user_name ? (
                          <>
                            <Typography variant="body2">{row.sub_user_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.sub_user_email}</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Admin User</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={row.module} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.message}</Typography>
                      </TableCell>
                      <TableCell>
                        {row.create_date ? new Date(row.create_date).toLocaleString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToastState({ ...toastState, open: false })}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ActivityLogs;
